/**
 * Every word the kit shows or announces on its own (REQUESTS #141 a, D22b): required markers, close and dismiss
 * buttons, copy feedback, status chips, the SRP field rows, the review card, the notification centre, the countdowns
 * and so on. English defaults; an app fills them from `design/i18n/<tag>.json` with `kitLabelKeys` (label → catalogue
 * key) and passes them to `KitCopyProvider` (nestable, so a subtree can override some). A component's own copy prop
 * (e.g. `DropZone label`, `Select placeholder`, `StatusChip label`, `Field requiredLabel`, the `labels` props of the
 * countdowns and the workspace switcher) wins over the provider. `{name}` placeholders are filled by the kit: give it
 * the raw message, not a filled one; `<b>…</b>` is rendered bold. The defaults equal the en.json values
 * (kit-labels.test.ts).
 *
 * Pairs named `…Single` / `…Multiple` are the English singular and plural (the kit picks `…Single` for a count of 1).
 * A language with more plural forms translates `…Multiple` in a form that fits every other count ("label: {count}").
 *
 * Components with a copy object of their own (`Chat` `chatLabels`, `AutomationCard` `automationCardLabels`,
 * `SignaturePad` `signaturePadLabels`, `DocumentEditorLayout` `documentEditorLabels`) keep it: pass it as before.
 */
export interface KitLabels {
  // Field, inputs
  /** Field: the marker after a required field's label; SRP field rows: the requirement and the placeholder. */
  required: string;
  /** Select / MultiSelect placeholder. */
  choose: string;
  /** MultiSelect button once something is chosen: `{count}`. */
  countSelected: string;
  /** MultiSelect: the list of chosen chips, `{label}` the select's label. */
  labelSelected: string;
  /** MultiSelect chip button: `{label}` the option. */
  removeLabel: string;
  /** Character counter at ≥ 90 % (after "n / limit"). */
  closeToLimit: string;
  /** Character counter over the limit: `{count}` characters over. */
  overLimit: string;
  /** DateTimeInput: under the input, `{value}` the UTC value. */
  srpValue: string;
  /** DateTimeInput: under an empty input. */
  srpStoresUtc: string;
  /** DropZone (idle). */
  dropFile: string;
  dropHint: string;
  releaseToUpload: string;
  /** DropZone: `{file}`. */
  uploadingFile: string;
  /** DropZone: `{file}`. */
  fileUploaded: string;
  chooseFileAgain: string;

  // Copy, recovery codes
  /** Copy button name: `{label}` what is copied, e.g. "field 2, Title". */
  copyLabel: string;
  /** Copy button name and status after copying: `{label}`. */
  copiedLabel: string;
  /** Copy failed; the text was selected. */
  copyFailedSelected: string;
  /** Copy failed; nothing selected. */
  copyFailed: string;
  /** Recovery codes: the copy-all button after copying. */
  copied: string;
  copyAll: string;
  downloadTxt: string;
  /** Recovery codes list name. */
  recoveryCodes: string;
  /** The first line of the downloaded recovery-codes file. */
  recoveryCodesFile: string;

  // Dialogs, banners, toasts
  close: string;
  closePanel: string;
  dismiss: string;
  cancel: string;
  confirm: string;
  /** Typed confirmation: `{phrase}` inside `<b>` (monospace). */
  typeToConfirm: string;
  /** Step-up dialog title. */
  confirmItsYou: string;
  /** Step-up dialog: `{action}`, e.g. "Mark the early warning as submitted". */
  auditNote: string;
  usePasskey: string;
  waitingForPasskey: string;
  useAuthenticatorCode: string;
  useRecoveryCode: string;
  usePasskeyInstead: string;
  /** Step-up code field (TOTP). */
  authenticatorCode: string;
  /** Step-up code field (recovery). */
  recoveryCode: string;
  /** Field help button: `{id}`, `{label}`. */
  aboutField: string;
  /** Test-workspace stripe: its region name. */
  testWorkspace: string;
  /** Test-workspace stripe: its text. */
  testWorkspaceStripe: string;

  // Shell
  sidebar: string;
  collapseSidebar: string;
  expandSidebar: string;
  /** The main navigation's name. */
  mainNav: string;
  /** After a nav badge's number (screen readers). */
  open: string;
  /** A nav item's open count (collapsed sidebar, phone tab bar): `{count}`. */
  countOpen: string;
  /** Workspace switcher button: its screen-reader name. */
  switchWorkspace: string;
  /** Workspace switcher menu: the line under a Test workspace. */
  testWorkspaceNote: string;
  notifications: string;
  /** The bell's name: `{unread}`. */
  notificationsUnread: string;
  /** Before an unread notification's title (screen readers). */
  unread: string;
  markAllRead: string;
  notificationSettings: string;
  seeAll: string;

  // Chips
  sevExploited: string;
  sevHigh: string;
  sevMedium: string;
  sevLow: string;
  notAffected: string;
  noKnownIssues: string;
  statusDraft: string;
  readyToSend: string;
  statusSubmitted: string;
  statusOverdue: string;
  statusClosed: string;
  statusNew: string;
  needsTriage: string;
  statusAffected: string;
  statusFixed: string;
  notStarted: string;
  needsReview: string;
  statusReviewed: string;
  statusSigned: string;
  needsSignature: string;
  expiringSoon: string;
  /** Plan badge during the trial: `{trialDaysLeft}`. */
  trialDaysLeft: string;
  confidenceHigh: string;
  confidenceMedium: string;
  confidenceLow: string;
  /** Component row: detection methods (API §13 `method`). */
  methodPackage: string;
  methodFingerprint: string;
  methodImported: string;
  methodManual: string;

  // Cards, charts, data
  /** Plan card name: `{name}`. */
  namePlan: string;
  recommended: string;
  yourPlan: string;
  /** Plan card button: `{name}`. */
  chooseName: string;
  /** Source card, above its settings: `{title}`. */
  setUpTitle: string;
  /** Product card sparkline name: `{name}`. */
  findingsSpark: string;
  /** Timeline list name (default). */
  timeline: string;
  added: string;
  removed: string;
  changed: string;
  /** Version diff list name. */
  componentChanges: string;
  /** Version diff, a changed component (screen readers): `{from}`, `{to}`. */
  diffFromTo: string;
  download: string;
  /** Draft chip (X-Brevalt-Draft). */
  draftNotReviewed: string;
  /** After a draft download link (screen readers). */
  draftNotReviewedSr: string;
  /** Next to every download (09 §1). */
  downloadDisclaimer: string;
  /** Attachment: `{name}` who uploaded it. */
  uploadedBy: string;
  /** Attachment: why it is not previewed. */
  notOpenedInBrevalt: string;

  // Flows
  /** Step indicator name (default). */
  progress: string;
  /** After a done step or checklist item (screen readers). */
  done: string;
  /** Progress steps, a step's state (screen readers). */
  stepRunning: string;
  stepPending: string;
  stepDone: string;
  stepFailed: string;
  stepSkipped: string;
  finishSetup: string;
  /** Setup checklist: `{done}`, `{total}`. */
  doneOfTotal: string;
  setupProgress: string;
  hideForNow: string;
  /** Password strength levels 0–4. */
  tooShort: string;
  passwordWeak: string;
  passwordFair: string;
  passwordGood: string;
  passwordStrong: string;
  /** Password strength line: `{strength}` (one of the levels) inside `<b>`. */
  passwordStrength: string;
  /** Scan progress region: `{fileName}`. */
  scanningFile: string;
  /** Scan progress: `{identified}`, `{total}`. */
  componentsIdentified: string;
  scanProgress: string;

  // Countdowns
  /** Countdown pill, visible before the clock: `{label}` the stage, e.g. "Early warning". */
  dueIn: string;
  /** Countdown pill once overdue: `{label}`. */
  overdueBy: string;
  /** Countdowns' live region: `{label}`, `{time}` the time left in words ("23 hours 12 minutes"). */
  dueInTime: string;
  /** Countdowns' live region once overdue: `{label}`, `{time}`. */
  overdueByTime: string;
  /** Under 6 hours left (case countdown status; appended to the live region as a sentence). */
  soon: string;
  /** Overdue (case countdown status; appended to the live region as a sentence). */
  passed: string;
  /** More than 6 hours left (case countdown status). */
  onTrack: string;
  /** Case countdown: under the clock while time is left. */
  left: string;
  /** Case countdown: under the clock once overdue. */
  overdue: string;
  /** Case countdown: `{local}` the due time in the viewer's zone (inside `<b>`), `{utc}` the same in UTC. */
  dueLocalUtc: string;
  /** Countdown pill link name: `{label}`, `{local}`, `{utc}`, `{caseId}`. */
  pillAria: string;

  // Stage stepper
  reportStages: string;
  draftReady: string;
  startsAfterPrevious: string;
  unlocksWhenFix: string;

  // SRP field rows (edit, copy pack, decision)
  requiredIfAvailable: string;
  optional: string;
  /** Placeholder of an optional long field. */
  optionalLeaveEmpty: string;
  /** Empty date or choice. */
  notSet: string;
  /** A read-only field's value (and the review line's). */
  filledBySrp: string;
  /** Copy pack: a read-only field. */
  filledBySrpRegistration: string;
  /** Copy pack: an empty value. */
  emptyLeaveBlank: string;
  /** Copy pack tick. */
  enteredInSrp: string;
  /** After the field's label (screen readers; the label comes first): `{id}`. */
  fieldNumber: string;
  /** Tooltip of the field number tag after a field's label. */
  fieldNumberHint: string;
  /** What a copy button copies, label first: `{label}`, `{id}`. */
  fieldIdLabel: string;
  /** SRP field rows, date values: `{value}` the date in the viewer's zone. */
  yourTimeValue: string;
  /** Yes/no fields: the words shown (the value stays the SRP's "Yes" / "No"). */
  yes: string;
  no: string;
  answered: string;
  yourAnswer: string;
  /** Decision field: next to the suggested choice. */
  suggested: string;

  // Review card, "What will be sent", optional details
  /** `{stage}`. */
  preparedByBrevalt: string;
  /** `{requiredTotal}`. */
  allRequiredReady: string;
  /** `{ready}`, `{total}`. */
  someRequiredReady: string;
  /** `{count}` (1). */
  needsAnswerSingle: string;
  /** `{count}` (any other count). */
  needsAnswerMultiple: string;
  /** `{stage}` (lower case). */
  nothingElseNeeded: string;
  /** `{stage}` (lower case). */
  everythingElseFilled: string;
  /** Review card, under the headline (default; the `sourcesLine` prop wins). */
  sourcesLine: string;
  /** `{stage}` (lower case). */
  reviewedCorrect: string;
  youStayResponsible: string;
  copyForSrp: string;
  fillWithExtension: string;
  pdfJson: string;
  youPressSubmit: string;
  answerQuestionSingle: string;
  answerQuestionsMultiple: string;
  tickTheBox: string;
  /** Hint under the buttons, gate `approval-required` (4-eyes). */
  approvalRequired: string;
  /** Hint under the buttons, gate `changed-since-review`. */
  changedSinceReview: string;
  /** Hint under the buttons while the tick can't be ticked (`reviewDisabled`): never "tick the box". */
  reviewNeededFirst: string;
  empty: string;
  /** Lock icon name: `{sourceText}`. */
  lockedSource: string;
  /** Lock icon name of a field the SRP fills. */
  lockedSrp: string;
  /** Edit button: `{id}`, `{label}`. */
  editField: string;
  whatWillBeSent: string;
  /** `{ready}`, `{total}`. */
  requiredReadyCount: string;
  /** `{count}` (1). */
  extraDetailSingle: string;
  /** `{count}` (any other count). */
  extraDetailsMultiple: string;
  hide: string;
  show: string;
  fieldsThatWillBeSent: string;
  /** `{count}`. */
  addOptionalDetails: string;
  /** `{stage}` (lower case). */
  enisaNotRequired: string;
  optionalDetails: string;
  /** `{value}`, `{reason}`. */
  suggestionReason: string;
  /** Add button name: `{id}`, `{label}`, `{value}`. */
  addSuggestion: string;
  add: string;
}

export const kitLabels: KitLabels = {
  required: "Required",
  choose: "Choose…",
  countSelected: "{count} selected",
  labelSelected: "{label}: selected",
  removeLabel: "Remove {label}",
  closeToLimit: "· close to the limit",
  overLimit: "· {count} over the limit",
  srpValue: "SRP value: {value}",
  srpStoresUtc: "Shown in your time zone; the SRP stores UTC.",
  dropFile: "Drop a file here or choose one",
  dropHint: "Up to 2 GB · deleted after scanning",
  releaseToUpload: "Release to upload",
  uploadingFile: "Uploading {file}",
  fileUploaded: "{file} uploaded",
  chooseFileAgain: "Choose the file again",

  copyLabel: "Copy {label}",
  copiedLabel: "Copied {label}",
  copyFailedSelected: "Couldn't copy. The text is selected: press Ctrl C or ⌘ C.",
  copyFailed: "Couldn't copy. Select the text and press Ctrl C or ⌘ C.",
  copied: "Copied",
  copyAll: "Copy all",
  downloadTxt: "Download .txt",
  recoveryCodes: "Recovery codes",
  recoveryCodesFile: "Brevalt recovery codes",

  close: "Close",
  closePanel: "Close panel",
  dismiss: "Dismiss",
  cancel: "Cancel",
  confirm: "Confirm",
  typeToConfirm: "Type <b>{phrase}</b> to confirm",
  confirmItsYou: "Confirm it's you",
  auditNote: "{action}. This is recorded in the audit log with your name and the time.",
  usePasskey: "Use passkey",
  waitingForPasskey: "Waiting for your passkey…",
  useAuthenticatorCode: "Use an authenticator code",
  useRecoveryCode: "Use a recovery code",
  usePasskeyInstead: "Use passkey instead",
  authenticatorCode: "6-digit code from your authenticator app",
  recoveryCode: "Recovery code",
  aboutField: "About field {id}, {label}",
  testWorkspace: "Test workspace",
  testWorkspaceStripe: "Test workspace: nothing here is sent anywhere",

  sidebar: "Sidebar",
  collapseSidebar: "Collapse sidebar",
  expandSidebar: "Expand sidebar",
  mainNav: "Main",
  open: "open",
  countOpen: "{count} open",
  switchWorkspace: "Switch workspace",
  testWorkspaceNote: "Nothing here is sent anywhere",
  notifications: "Notifications",
  notificationsUnread: "Notifications, {unread} unread",
  unread: "Unread:",
  markAllRead: "Mark all as read",
  notificationSettings: "Notification settings",
  seeAll: "See all",

  sevExploited: "Actively exploited",
  sevHigh: "High",
  sevMedium: "Medium",
  sevLow: "Low",
  notAffected: "Not affected",
  noKnownIssues: "No known issues",
  statusDraft: "Draft",
  readyToSend: "Ready to send",
  statusSubmitted: "Submitted",
  statusOverdue: "Overdue",
  statusClosed: "Closed",
  statusNew: "New",
  needsTriage: "Needs triage",
  statusAffected: "Affected",
  statusFixed: "Fixed",
  notStarted: "Not started",
  needsReview: "Needs review",
  statusReviewed: "Reviewed",
  statusSigned: "Signed",
  needsSignature: "Needs signature",
  expiringSoon: "Expiring soon",
  trialDaysLeft: "Trial · {trialDaysLeft} days left",
  confidenceHigh: "High confidence",
  confidenceMedium: "Medium confidence",
  confidenceLow: "Low confidence",
  methodPackage: "package metadata",
  methodFingerprint: "binary fingerprint",
  methodImported: "SBOM import",
  methodManual: "added by hand",

  namePlan: "{name} plan",
  recommended: "Recommended",
  yourPlan: "Your plan",
  chooseName: "Choose {name}",
  setUpTitle: "Set up {title}",
  findingsSpark: "Findings over the last 8 weeks for {name}",
  timeline: "Timeline",
  added: "Added",
  removed: "Removed",
  changed: "Changed",
  componentChanges: "Component changes",
  diffFromTo: "{from} to {to}",
  download: "Download",
  draftNotReviewed: "DRAFT — not reviewed",
  draftNotReviewedSr: "(draft, not reviewed)",
  downloadDisclaimer: "Generated by Brevalt from your data. You are responsible for its accuracy and for submission.",
  uploadedBy: "uploaded by {name}",
  notOpenedInBrevalt: "not opened in Brevalt, download to view",

  progress: "Progress",
  done: "(done)",
  stepRunning: "in progress",
  stepPending: "pending",
  stepDone: "done",
  stepFailed: "failed",
  stepSkipped: "skipped",
  finishSetup: "Finish setup",
  doneOfTotal: "{done} of {total} done",
  setupProgress: "Setup progress",
  hideForNow: "Hide for now",
  tooShort: "Too short",
  passwordWeak: "Weak",
  passwordFair: "Fair",
  passwordGood: "Good",
  passwordStrong: "Strong",
  passwordStrength: "Password strength: <b>{strength}</b>. Use 12+ characters; a short sentence works well.",
  scanningFile: "Scanning {fileName}",
  componentsIdentified: "{identified} of {total} components identified",
  scanProgress: "Scan progress",

  dueIn: "{label} due in",
  overdueBy: "{label} overdue by",
  dueInTime: "{label} due in {time}",
  overdueByTime: "{label} overdue by {time}",
  soon: "Less than 6 hours left",
  passed: "The deadline has passed",
  onTrack: "On track",
  left: "left",
  overdue: "overdue",
  dueLocalUtc: "Due <b>{local}</b> your time · {utc}",
  pillAria: "{label} due {local} ({utc}), case {caseId}. Open case.",

  reportStages: "Report stages",
  draftReady: "Draft ready",
  startsAfterPrevious: "Starts after the previous stage",
  unlocksWhenFix: "Unlocks when a fix is available",

  requiredIfAvailable: "Required if available",
  optional: "Optional",
  optionalLeaveEmpty: "Optional. Leave it empty if you don't know yet",
  notSet: "Not set",
  filledBySrp: "Filled by the SRP",
  filledBySrpRegistration: "Filled by the SRP from your registration",
  emptyLeaveBlank: "Empty, leave it blank in the SRP",
  enteredInSrp: "Entered in the SRP",
  fieldNumber: "(field {id})",
  fieldNumberHint: "Field number in ENISA's SRP glossary; the SRP form uses the same numbers",
  fieldIdLabel: "{label} (field {id})",
  yourTimeValue: "Your time: {value}",
  yes: "Yes",
  no: "No",
  answered: "Answered",
  yourAnswer: "Your answer",
  suggested: "suggested",

  preparedByBrevalt: "{stage} · prepared by Brevalt",
  allRequiredReady: "All {requiredTotal} required fields are ready.",
  someRequiredReady: "{ready} of {total} required fields are ready.",
  needsAnswerSingle: "{count} needs your answer.",
  needsAnswerMultiple: "{count} need your answer.",
  nothingElseNeeded: "Nothing else is needed for the {stage}.",
  everythingElseFilled: "Everything else needed for the {stage} is filled.",
  sourcesLine: "Filled from your SBOM, CISA KEV and product profile.",
  reviewedCorrect: "I've reviewed the {stage} and it is correct.",
  youStayResponsible: "You stay responsible for what you send.",
  copyForSrp: "Copy for SRP",
  fillWithExtension: "Fill with extension",
  pdfJson: "PDF / JSON",
  youPressSubmit: "You press Submit in the SRP portal. Brevalt never submits for you.",
  answerQuestionSingle: "Answer the question above and tick the box to continue.",
  answerQuestionsMultiple: "Answer the questions above and tick the box to continue.",
  tickTheBox: "Tick the box to continue.",
  approvalRequired: "A member who may approve reports has to approve it before you can send it.",
  changedSinceReview: "The draft changed after the last review: tick the box again to continue.",
  reviewNeededFirst: "You can copy or fill once this stage is reviewed.",
  empty: "Empty",
  lockedSource: "Locked: {sourceText}",
  lockedSrp: "Locked: filled by the SRP",
  editField: "Edit field {id}, {label}",
  whatWillBeSent: "What will be sent",
  requiredReadyCount: "{ready} of {total} required fields ready",
  extraDetailSingle: "{count} extra detail from your data",
  extraDetailsMultiple: "{count} extra details from your data",
  hide: "Hide",
  show: "Show",
  fieldsThatWillBeSent: "Fields that will be sent",
  addOptionalDetails: "Add optional details ({count})",
  enisaNotRequired: "ENISA does not require these for the {stage}. Add them only if you know them.",
  optionalDetails: "Optional details",
  suggestionReason: "Suggestion: “{value}”. {reason}",
  addSuggestion: "Add suggestion for field {id}, {label}: {value}",
  add: "Add",
};

/** Each label's key in `design/i18n/en.json` (translated in every `<tag>.json`). */
export const kitLabelKeys: Record<keyof KitLabels, string> = {
  required: "ui.required",
  choose: "ui.choose",
  countSelected: "ui.count_selected",
  labelSelected: "ui.label_selected",
  removeLabel: "ui.remove_label",
  closeToLimit: "ui.close_to_the_limit",
  overLimit: "ui.count_over_the_limit",
  srpValue: "ui.srp_value_value",
  srpStoresUtc: "ui.shown_in_your_time_zone_srp_stores_utc",
  dropFile: "ui.drop_a_file_here_or_choose",
  dropHint: "ui.up_to_2_gb_deleted_after",
  releaseToUpload: "ui.release_to_upload",
  uploadingFile: "ui.uploading_file",
  fileUploaded: "ui.file_uploaded",
  chooseFileAgain: "ui.choose_the_file_again",

  copyLabel: "ui.copy_label",
  copiedLabel: "ui.copied_label",
  copyFailedSelected: "ui.couldn_t_copy_the_text_is",
  copyFailed: "ui.couldn_t_copy_select_the_text",
  copied: "ui.copied",
  copyAll: "ui.copy_all",
  downloadTxt: "ui.download_txt",
  recoveryCodes: "ui.recovery_codes",
  recoveryCodesFile: "ui.recovery_codes_file_title",

  close: "ui.close",
  closePanel: "ui.close_panel",
  dismiss: "ui.dismiss",
  cancel: "ui.cancel",
  confirm: "ui.confirm",
  typeToConfirm: "ui.type_phrase_to_confirm",
  confirmItsYou: "ui.confirm_it_s_you",
  auditNote: "ui.step_up_audit_note",
  usePasskey: "ui.use_passkey",
  waitingForPasskey: "ui.waiting_for_your_passkey",
  useAuthenticatorCode: "ui.use_an_authenticator_code",
  useRecoveryCode: "ui.use_a_recovery_code",
  usePasskeyInstead: "ui.use_passkey_instead",
  authenticatorCode: "ui.six_digit_authenticator_code",
  recoveryCode: "ui.recovery_code",
  aboutField: "ui.about_field_id_label",
  testWorkspace: "ui.test_workspace",
  testWorkspaceStripe: "ui.test_workspace_nothing_here_is_sent",

  sidebar: "ui.sidebar",
  collapseSidebar: "ui.collapse_sidebar",
  expandSidebar: "ui.expand_sidebar",
  mainNav: "ui.main",
  open: "ui.open",
  countOpen: "ui.count_open",
  switchWorkspace: "ui.switch_workspace",
  testWorkspaceNote: "ui.nothing_here_is_sent_anywhere",
  notifications: "ui.notifications",
  notificationsUnread: "ui.notifications_unread_unread",
  unread: "ui.unread",
  markAllRead: "ui.mark_all_as_read",
  notificationSettings: "ui.notification_settings",
  seeAll: "ui.see_all",

  sevExploited: "ui.actively_exploited",
  sevHigh: "ui.severity_high",
  sevMedium: "ui.severity_medium",
  sevLow: "ui.severity_low",
  notAffected: "ui.not_affected",
  noKnownIssues: "ui.no_known_issues",
  statusDraft: "ui.status_draft",
  readyToSend: "ui.ready_to_send",
  statusSubmitted: "ui.status_submitted",
  statusOverdue: "ui.status_overdue",
  statusClosed: "ui.status_closed",
  statusNew: "ui.status_new",
  needsTriage: "ui.needs_triage",
  statusAffected: "ui.status_affected",
  statusFixed: "ui.status_fixed",
  notStarted: "ui.not_started",
  needsReview: "ui.needs_review",
  statusReviewed: "ui.status_reviewed",
  statusSigned: "ui.status_signed",
  needsSignature: "ui.needs_signature",
  expiringSoon: "ui.expiring_soon",
  trialDaysLeft: "ui.trial_trialdaysleft_days_left",
  confidenceHigh: "ui.confidence_high",
  confidenceMedium: "ui.confidence_medium",
  confidenceLow: "ui.confidence_low",
  methodPackage: "ui.method_package_metadata",
  methodFingerprint: "ui.method_binary_fingerprint",
  methodImported: "ui.sbom_import",
  methodManual: "ui.method_added_by_hand",

  namePlan: "ui.name_plan",
  recommended: "ui.recommended",
  yourPlan: "ui.your_plan",
  chooseName: "ui.choose_name",
  setUpTitle: "ui.set_up_title",
  findingsSpark: "ui.findings_over_the_last_8_weeks",
  timeline: "ui.timeline",
  added: "ui.added",
  removed: "ui.removed",
  changed: "ui.changed",
  componentChanges: "ui.component_changes",
  diffFromTo: "ui.diff_from_to",
  download: "ui.download",
  draftNotReviewed: "ui.draft_not_reviewed",
  draftNotReviewedSr: "ui.draft_not_reviewed_2",
  downloadDisclaimer: "ui.generated_by_brevalt_from_your_data",
  uploadedBy: "ui.attachment_uploaded_by",
  notOpenedInBrevalt: "ui.attachment_not_opened",

  progress: "ui.progress",
  done: "ui.done",
  stepRunning: "ui.step_in_progress",
  stepPending: "ui.step_pending",
  stepDone: "ui.step_done",
  stepFailed: "ui.step_failed",
  stepSkipped: "ui.step_skipped",
  finishSetup: "ui.finish_setup",
  doneOfTotal: "ui.done_of_total",
  setupProgress: "ui.setup_progress",
  hideForNow: "ui.hide_for_now",
  tooShort: "ui.too_short",
  passwordWeak: "ui.password_weak",
  passwordFair: "ui.password_fair",
  passwordGood: "ui.password_good",
  passwordStrong: "ui.password_strong",
  passwordStrength: "ui.password_strength_hint",
  scanningFile: "ui.scanning_filename",
  componentsIdentified: "ui.components_identified",
  scanProgress: "ui.scan_progress",

  dueIn: "shell.label_due_in",
  overdueBy: "shell.label_overdue_by",
  dueInTime: "shell.label_due_in_time",
  overdueByTime: "shell.label_overdue_by_time",
  soon: "ui.less_than_6_hours_left",
  passed: "ui.the_deadline_has_passed",
  onTrack: "ui.on_track",
  left: "ui.countdown_left",
  overdue: "ui.countdown_overdue",
  dueLocalUtc: "ui.countdown_due_local_utc",
  pillAria: "ui.countdown_pill_aria",

  reportStages: "ui.report_stages",
  draftReady: "ui.draft_ready",
  startsAfterPrevious: "ui.starts_after_the_previous_stage",
  unlocksWhenFix: "ui.unlocks_when_a_fix_is_available",

  requiredIfAvailable: "ui.required_if_available",
  optional: "ui.optional",
  optionalLeaveEmpty: "ui.optional_leave_it_empty_if_you",
  notSet: "ui.not_set",
  filledBySrp: "ui.filled_by_the_srp",
  filledBySrpRegistration: "ui.filled_by_the_srp_from_your",
  emptyLeaveBlank: "ui.empty_leave_it_blank_in_the",
  enteredInSrp: "ui.entered_in_the_srp",
  fieldNumber: "ui.field_number",
  fieldNumberHint: "ui.field_number_hint",
  fieldIdLabel: "ui.field_id_label",
  yourTimeValue: "ui.your_time_value",
  yes: "ui.yes",
  no: "ui.no",
  answered: "ui.answered",
  yourAnswer: "ui.your_answer",
  suggested: "ui.suggested",

  preparedByBrevalt: "ui.stage_prepared_by_brevalt",
  allRequiredReady: "ui.all_requiredtotal_required_fields_are_ready",
  someRequiredReady: "ui.ready_of_total_required_fields_are_ready",
  needsAnswerSingle: "ui.needs_your_answer_single",
  needsAnswerMultiple: "ui.needs_your_answer_multiple",
  nothingElseNeeded: "ui.nothing_else_is_needed_for_the",
  everythingElseFilled: "ui.everything_else_needed_for_the_stage",
  sourcesLine: "ui.filled_from_your_sbom_cisa_kev",
  reviewedCorrect: "ui.reviewed_stage_correct",
  youStayResponsible: "ui.you_stay_responsible_for_what_you",
  copyForSrp: "ui.copy_for_srp",
  fillWithExtension: "ui.fill_with_extension",
  pdfJson: "ui.pdf_json",
  youPressSubmit: "ui.you_press_submit_in_the_srp",
  answerQuestionSingle: "ui.answer_the_question_above_single",
  answerQuestionsMultiple: "ui.answer_the_questions_above_multiple",
  tickTheBox: "ui.tick_the_box_to_continue",
  approvalRequired: "ui.approval_required_before_send",
  changedSinceReview: "ui.changed_since_review_tick_again",
  reviewNeededFirst: "ui.copy_or_fill_once_reviewed",
  empty: "ui.empty",
  lockedSource: "ui.locked_sourcetext",
  lockedSrp: "ui.locked_filled_by_the_srp",
  editField: "ui.edit_field_id_label",
  whatWillBeSent: "ui.what_will_be_sent",
  requiredReadyCount: "ui.ready_of_total_required_fields",
  extraDetailSingle: "ui.extra_details_single",
  extraDetailsMultiple: "ui.extra_details_multiple",
  hide: "ui.hide",
  show: "ui.show",
  fieldsThatWillBeSent: "ui.fields_that_will_be_sent",
  addOptionalDetails: "ui.add_optional_details_count",
  enisaNotRequired: "ui.enisa_does_not_require_these_stage",
  optionalDetails: "ui.optional_details",
  suggestionReason: "ui.suggestion_value_reason",
  addSuggestion: "ui.add_suggestion_for_field_id_label",
  add: "ui.add",
};

/** `…Single` for a count of 1, else `…Multiple` (see the note on plural pairs above). */
export const pickCount = (count: number, single: string, multiple: string): string => (count === 1 ? single : multiple);
